import PropTypes from 'prop-types';
import { filter, find, hasIn, pluck, propEq } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';

import matchMobile from '../helpers/util/match-mobile';
import { Container, HotSpot, LinearNavigation, Map } from '../index';

import styles from './hotspot-map.module.scss';

function HotSpotMap({ maps, isComplete, onComplete, onProgress }) {
  const allSpots = useMemo(
    () =>
      maps.reduce((memo, imageMap) => {
        imageMap.hotspots.forEach((spot) => {
          memo[spot.id] = false;
        });

        return memo;
      }, {}),
    [maps],
  );

  const [spotsState, setSpotsState] = useState(allSpots);
  const [isMobile, setIsMobile] = useState(matchMobile.isMobile);
  const [current, setCurrent] = useState(0);
  const hasMobileView = useMemo(() => !!find(hasIn('mobile'), maps), [maps]);
  const filteredMaps = useMemo(
    () => (hasMobileView ? filter(propEq('mobile', isMobile), maps) : [...maps]),
    [hasMobileView, isMobile, maps],
  );

  const [currentMap, setCurrentMap] = useState(filteredMaps[current]);
  const totalCount = filteredMaps.length;

  const back = () => {
    if (current === 0) {
      return;
    }
    setCurrent(current - 1);
  };
  const next = () => {
    if (!currentMap.completed || current === totalCount - 1) {
      return;
    }
    setCurrent(current + 1);
  };

  const getMaps = () => {
    const i = [...filteredMaps];
    return i.slice(current, totalCount);
  };

  const hotspotDone = (id) => {
    const state = { ...spotsState };
    state[id] = true;
    setSpotsState(state);

    const values = Object.values(state);
    const total = values.length;
    const completed = values.filter((i) => i).length;

    onProgress(completed / total);

    if (completed === total) {
      onComplete();
    }
  };

  useEffect(() => {
    const handleMediaChange = (matches) => {
      setCurrent(0);
      setIsMobile(matches);
    };

    matchMobile.addListener(handleMediaChange);

    return () => matchMobile.removeListener(handleMediaChange);
  }, []);

  useEffect(() => {
    setCurrentMap({ ...filteredMaps[current] });
  }, [filteredMaps, current]);

  useEffect(() => {
    const map = filteredMaps[current];
    const mapCompleted = !pluck('id', map.hotspots).find((i) => !spotsState[i]);

    if (mapCompleted) {
      map.completed = true;
      setCurrentMap({ ...map });
    }
  }, [current, filteredMaps, spotsState]);

  return (
    <Container wide={true}>
      <div className={styles['hotspot-map']}>
        {getMaps().map((imageMap) => (
          <Map
            key={imageMap.id}
            background={imageMap.background}
            backgroundColor={imageMap.backgroundColor}
            isCurrent={currentMap.id === imageMap.id}
            padding={imageMap.padding}
            identifier={imageMap.identifier}
          >
            {imageMap.hotspots.map((spot) => (
              <HotSpot
                key={spot.id}
                top={spot.coords.top}
                left={spot.coords.left}
                modal={{
                  ctaText: spot.buttonText,
                  image: spot.image,
                  text: spot.desc,
                  title: spot.title,
                }}
                tooltip={spot.tooltip}
                isComplete={spotsState[spot.id]}
                onDone={() => hotspotDone(spot.id)}
              />
            ))}
          </Map>
        ))}
        <div className={`${styles.nav} ${isComplete && styles.completed}`}>
          <LinearNavigation
            item={currentMap}
            allItems={filteredMaps}
            onBack={back}
            onNext={next}
            isStrict={true}
          />
        </div>
      </div>
    </Container>
  );
}

HotSpotMap.propTypes = {
  maps: PropTypes.array.isRequired,
  isComplete: PropTypes.bool,
  onComplete: PropTypes.func,
  onProgress: PropTypes.func,
};

HotSpotMap.defaultProps = {
  modalType: 'circle',
  isComplete: false,
  onComplete: () => {},
};

export default HotSpotMap;
